import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { platform } from '@/utils/platform';
import { nativeAppleSignIn, isNativeAppleAuthAvailable } from '@/capacitor/plugins/apple-auth';
import { useAuth } from '../hooks/useAuth';

export interface AppleSignInButtonProps {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

const AppleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
  </svg>
);

/**
 * Native Apple Sign-In button — uses @capgo/capacitor-social-login SDK.
 * Only rendered on native iOS.
 */
function NativeAppleSignInButton({ onError }: AppleSignInButtonProps) {
  const { t } = useTranslation();
  const { appleSignIn } = useAuth();
  const [loading, setLoading] = useState(false);

  if (!isNativeAppleAuthAvailable()) return null;

  const handleClick = async () => {
    try {
      setLoading(true);
      const result = await nativeAppleSignIn();
      appleSignIn({ identityToken: result.identityToken, user: result.user });
    } catch (err) {
      onError?.(err instanceof Error ? err : new Error('Native Apple sign-in failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full">
      <button
        type="button"
        onClick={handleClick}
        disabled={loading}
        className="glass-btn w-full flex items-center justify-center gap-3 rounded-xl py-3 px-4 text-sm font-bold transition-all active:scale-[0.98] cursor-pointer disabled:opacity-60"
        style={{
          backgroundColor: '#000000',
          border: '1px solid #000000',
          color: '#ffffff',
        }}
      >
        <AppleIcon />
        {t('auth.signInWithApple', 'Sign in with Apple')}
      </button>
    </div>
  );
}

/**
 * Apple Sign-In button — iOS native only.
 * Apple requires Sign in with Apple only when other third-party sign-in is present on iOS.
 * On web, we don't need it.
 */
export const AppleSignInButton = (props: AppleSignInButtonProps) => {
  if (platform.isNative && platform.isIOS) return <NativeAppleSignInButton {...props} />;
  return null;
};
